import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Catalog, LanguageApp, languagesAPI } from "../api";
import { useI18n } from "../../../i18n";

export const useGetLanguages = () =>
  useQuery({
    queryKey: ["languages"],
    queryFn: languagesAPI.getLanguages,
  });

export const useDeleteLanguage = () => {
  const queryClient = useQueryClient();
  const { t, reload } = useI18n();

  return useMutation({
    mutationFn: languagesAPI.deleteLanguage,
    onSuccess: () => {
      toast.success(t("language.toast.removed"));
      reload();
      // Returned so the mutation stays pending until the row is gone from the list.
      return queryClient.invalidateQueries({ queryKey: ["languages"] });
    },
    onError: (error: any) => {
      toast.error(t("language.toast.removeFailed"));
    },
  });
};

/** Creates the language when `isNew`, then replaces whichever catalogs were picked. */
export const useSaveLanguage = () => {
  const queryClient = useQueryClient();
  const { t, reload } = useI18n();

  return useMutation({
    mutationFn: async ({
      code,
      name,
      isNew,
      catalogs,
    }: {
      code: string;
      name: string;
      isNew: boolean;
      catalogs: Partial<Record<LanguageApp, Catalog>>;
    }) => {
      if (isNew) await languagesAPI.addLanguage({ code, name });
      for (const [app, catalog] of Object.entries(catalogs)) {
        await languagesAPI.saveCatalog(app as LanguageApp, code, catalog!);
      }
    },
    onSuccess: (_, { isNew }) => {
      toast.success(t(isNew ? "language.toast.added" : "language.toast.uploaded"));
      reload();
      return queryClient.invalidateQueries({ queryKey: ["languages"] });
    },
    onError: (_, { isNew }) => {
      toast.error(t(isNew ? "language.toast.addFailed" : "language.toast.uploadFailed"));
      // A failed upload after a successful create still leaves the language listed.
      queryClient.invalidateQueries({ queryKey: ["languages"] });
      reload();
    },
  });
};
